import React, { useContext, useState, lazy, Suspense } from 'react';
import { ShopProvider, ShopContext } from './context/ShopContext';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import VisitStoreBanner from './components/VisitStoreBanner';
import Home from './components/Home';
import CartDrawer from './components/CartDrawer';
import AuthModal from './components/AuthModal';
import InfoModal from './components/InfoModal';
import WhatsAppWidget from './components/WhatsAppWidget';
import './App.css';

// Code-splitting non-initial routes to minimize main bundle & speed up first load
const ProductCatalog = lazy(() => import('./components/ProductCatalog'));
const ProductDetail = lazy(() => import('./components/ProductDetail'));
const Checkout = lazy(() => import('./components/Checkout'));
const AdminPanel = lazy(() => import('./components/AdminPanel'));
const AdminAccessGate = lazy(() => import('./components/AdminAccessGate'));
const About = lazy(() => import('./components/About'));
const Account = lazy(() => import('./components/Account'));
const OurCustomers = lazy(() => import('./components/OurCustomers'));
const Blog = lazy(() => import('./components/Blog'));
const BlogPost = lazy(() => import('./components/BlogPost'));
const TrackOrder = lazy(() => import('./components/TrackOrder'));
const Sitemap = lazy(() => import('./components/Sitemap'));

function ViewLoader() {
  return (
    <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div className="spinner" style={{ width: 36, height: 36, border: '3px solid rgba(168,85,247,0.2)', borderTopColor: '#a855f7', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
    </div>
  );
}

function MainAppContent() {
  const { activeView, toast, user } = useContext(ShopContext);
  const [cartOpen, setCartOpen] = useState(false);

  React.useEffect(() => {
    const handleScrollReveal = () => {
      const targets = document.querySelectorAll('.reveal-el, .reveal-el-left, .reveal-el-right, .reveal-slide-up, .reveal-slide-down, .reveal-slide-left, .reveal-slide-right');
      
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('reveal-visible');
          }
        });
      }, {
        threshold: 0.01,
        rootMargin: '60px 0px 60px 0px'
      });

      targets.forEach(target => observer.observe(target));

      return () => {
        targets.forEach(target => observer.unobserve(target));
        observer.disconnect();
      };
    };

    const timer = setTimeout(handleScrollReveal, 120);
    return () => clearTimeout(timer);
  }, [activeView]);

  const renderView = () => {
    switch (activeView) {
      case 'home':
        return <Home />;
      case 'shop':
        return (
          <Suspense fallback={<ViewLoader />}>
            <ProductCatalog />
          </Suspense>
        );
      case 'product-detail':
        return (
          <Suspense fallback={<ViewLoader />}>
            <ProductDetail />
          </Suspense>
        );
      case 'checkout':
        return (
          <Suspense fallback={<ViewLoader />}>
            <Checkout />
          </Suspense>
        );
      case 'admin':
        return (
          <Suspense fallback={<ViewLoader />}>
            {user && user.role === 'admin' ? <AdminPanel /> : <AdminAccessGate />}
          </Suspense>
        );
      case 'account':
        return (
          <Suspense fallback={<ViewLoader />}>
            <Account />
          </Suspense>
        );
      case 'about':
        return (
          <Suspense fallback={<ViewLoader />}>
            <About />
          </Suspense>
        );
      case 'customers':
        return (
          <Suspense fallback={<ViewLoader />}>
            <OurCustomers />
          </Suspense>
        );
      case 'blog':
        return (
          <Suspense fallback={<ViewLoader />}>
            <Blog />
          </Suspense>
        );
      case 'blog-post':
        return (
          <Suspense fallback={<ViewLoader />}>
            <BlogPost />
          </Suspense>
        );
      case 'track-order':
        return (
          <Suspense fallback={<ViewLoader />}>
            <TrackOrder />
          </Suspense>
        );
      case 'sitemap':
        return (
          <Suspense fallback={<ViewLoader />}>
            <Sitemap />
          </Suspense>
        );
      default:
        return <Home />;
    }
  };

  return (
    <div className="App">
      {/* Toast Notification Popup */}
      <div className={`toast-notification glass ${toast.show ? 'show' : ''}`}>
        <span>{toast.message}</span>
      </div>

      <Navbar onCartClick={() => setCartOpen(true)} />
      
      <main className="main-content-layout">
        <div key={activeView} className="page-slide-transition-container page-slide-left-to-right">
          {renderView()}
        </div>
      </main>

      <CartDrawer isOpen={cartOpen} onClose={() => setCartOpen(false)} />
      <AuthModal />
      <InfoModal />
      <VisitStoreBanner />
      <Footer />
      <WhatsAppWidget />
    </div>
  );
}

function App() {
  return (
    <ShopProvider>
      <MainAppContent />
    </ShopProvider>
  );
}

export default App;
